import React, { useState } from "react";
import {
  Box,
  Link,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  Container,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate, useLocation } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Cookies from "js-cookie";
import axios from "axios";
import BASE_URL from "../../../config/apiConfig";
import { saveUserToken } from "../../../utils/auth";

const CuppatradeLogin = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState(state?.email || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const validateForm = () => {
    const isEmailValid = /\S+@\S+\.\S+/.test(email);
    const isPasswordValid = password.length >= 6;

    setEmailError(!isEmailValid);
    setPasswordError(!isPasswordValid);

    return isEmailValid && isPasswordValid;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/user/cuppatrade/login`, {
        email,
        password,
      });

      const token = res.data?.token;
      if (token) {
        saveUserToken(token, email);
        navigate("/user/home"); // ✅ Update this to match your real dashboard route
      } else {
        alert("Login response is missing token.");
      }
    } catch (err) {
      console.error("Login error:", err.response?.data);
      alert(err.response?.data?.message || "Login failed. Try again."); // ✅ Use `message` key
    } finally {
      setLoading(false);
    }
  };

  const renderForm = (
    <Box
      component="form"
      onSubmit={handleLogin}
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      sx={{ width: "100%", maxWidth: "400px", mb: 2 }}
    >
      <TextField
        fullWidth
        label="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={emailError}
        helperText={emailError ? "Please enter a valid email address" : ""}
        sx={{
          mb: 2,
          "& label.Mui-focused": { color: "#51833a" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#51833a" },
            "&.Mui-focused fieldset": { borderColor: "#51833a" },
          },
        }}
      />

      <TextField
        fullWidth
        label="Password"
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={passwordError}
        helperText={
          passwordError ? "Password must be at least 6 characters" : ""
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          "& label.Mui-focused": { color: "#51833a" },
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": { borderColor: "#51833a" },
            "&.Mui-focused fieldset": { borderColor: "#51833a" },
          },
        }}
      />

      <Box sx={{ textAlign: "right", mb: 2 }}>
        <Link
          href="/user/recoverpassword"
          variant="body2"
          sx={{
            fontSize: "0.9rem",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline", color: "#51833a" },
          }}
        >
          Forgot password?
        </Link>
      </Box>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        color="inherit"
        variant="contained"
        loading={loading}
        sx={{
          background: "linear-gradient(45deg, #51833a 30%, #66bb6a 90%)",
          color: "#fff",
          "&:hover": { backgroundColor: "#406027" },
          mb: 2,
          textTransform: "none",
          minHeight: "50px",
          borderRadius: "8px",
          transition: "0.3s ease",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        Sign in
      </LoadingButton>
    </Box>
  );

  return (
    <Container
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: { xs: 2, md: 0 },
        background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#fff",
          minHeight: "70vh",
          borderRadius: "16px",
          boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          maxWidth: "900px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            padding: { xs: 3, md: 5 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            backgroundColor: "#f9fafb",
          }}
        >
          <Box
            gap={1.5}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            sx={{ mb: 5 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#333",
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Sign in
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Don’t have an account?{" "}
              <Link
                href="/user/signup"
                variant="body2"
                sx={{
                  textDecoration: "none",
                  color: "#51833a",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Sign Up
              </Link>
            </Typography>
          </Box>

          {renderForm}
        </Box>

        <Box
          sx={{
            backgroundColor: "#e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
            padding: { xs: 2, md: 4 },
            position: "relative",
          }}
        >
          <Box
            component="img"
            src="/Images/Login_Body.webp"
            alt="Task illustration"
            sx={{ width: "100%", maxWidth: "300px", borderRadius: "12px" }}
          />
          <Box
            sx={{
              width: "120px",
              height: "120px",
              backgroundColor: "#51833a",
              borderRadius: "50%",
              position: "absolute",
              bottom: "-20px",
              right: "-20px",
              opacity: 0.7,
              zIndex: -1,
            }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default CuppatradeLogin;
